<!-- Tab bar -->
<div class="flex border-b border-slate-200 px-6" role="tablist">
  <button
    *ngFor="let tab of tabs"
    (click)="selectedTab = tab.id"
    role="tab"
    [attr.aria-selected]="selectedTab === tab.id"
    class="mr-6 border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none"
    [class.border-violet-600]="selectedTab === tab.id"
    [class.text-violet-700]="selectedTab === tab.id"
    [class.border-transparent]="selectedTab !== tab.id"
    [class.text-slate-500]="selectedTab !== tab.id"
    [class.hover:text-slate-700]="selectedTab !== tab.id"
  >
    {{ tab.label }}
  </button>
</div>

<!-- ── Overview ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">

  <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

    <!-- Functions -->
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Functions</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
    </div>

    <!-- Monitored -->
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
    </div>

    <!-- Errors -->
    <div class="rounded-lg border p-4"
         [class.border-red-200]="metricsState === 'loaded' && metrics !== null && metrics.errors > 0"
         [class.bg-red-50]="metricsState === 'loaded' && metrics !== null && metrics.errors > 0"
         [class.border-slate-200]="!(metricsState === 'loaded' && metrics !== null && metrics.errors > 0)"
         [class.bg-slate-50]="!(metricsState === 'loaded' && metrics !== null && metrics.errors > 0)">
      <p class="text-xs font-medium uppercase tracking-wide"
         [class.text-red-500]="metricsState === 'loaded' && metrics !== null && metrics.errors > 0"
         [class.text-slate-400]="!(metricsState === 'loaded' && metrics !== null && metrics.errors > 0)">Errors</p>
      <p class="mt-1 text-2xl font-bold"
         [class.text-red-700]="metricsState === 'loaded' && metrics !== null && metrics.errors > 0"
         [class.text-slate-900]="!(metricsState === 'loaded' && metrics !== null && metrics.errors > 0)">
        <ng-container *ngIf="metricsState === 'loading'">
          <span class="animate-pulse text-slate-300">—</span>
        </ng-container>
        <ng-container *ngIf="metricsState === 'loaded' && metrics !== null">
          {{ formatCount(metrics.errors) }}
        </ng-container>
        <ng-container *ngIf="metricsState === 'unavailable'">
          <span class="text-slate-300">—</span>
        </ng-container>
      </p>
      <p class="mt-1 text-[11px]"
         [class.text-red-400]="metricsState === 'loaded' && metrics !== null && metrics.errors > 0"
         [class.text-slate-400]="!(metricsState === 'loaded' && metrics !== null && metrics.errors > 0)">
        <ng-container *ngIf="metricsState === 'unavailable'">No metric data</ng-container>
        <ng-container *ngIf="metricsState !== 'unavailable'">Last 24 hours</ng-container>
      </p>
    </div>

    <!-- Throttles -->
    <div class="rounded-lg border p-4"
         [class.border-amber-200]="metricsState === 'loaded' && metrics !== null && metrics.throttles > 0"
         [class.bg-amber-50]="metricsState === 'loaded' && metrics !== null && metrics.throttles > 0"
         [class.border-slate-200]="!(metricsState === 'loaded' && metrics !== null && metrics.throttles > 0)"
         [class.bg-slate-50]="!(metricsState === 'loaded' && metrics !== null && metrics.throttles > 0)">
      <p class="text-xs font-medium uppercase tracking-wide"
         [class.text-amber-500]="metricsState === 'loaded' && metrics !== null && metrics.throttles > 0"
         [class.text-slate-400]="!(metricsState === 'loaded' && metrics !== null && metrics.throttles > 0)">Throttles</p>
      <p class="mt-1 text-2xl font-bold"
         [class.text-amber-700]="metricsState === 'loaded' && metrics !== null && metrics.throttles > 0"
         [class.text-slate-900]="!(metricsState === 'loaded' && metrics !== null && metrics.throttles > 0)">
        <ng-container *ngIf="metricsState === 'loading'">
          <span class="animate-pulse text-slate-300">—</span>
        </ng-container>
        <ng-container *ngIf="metricsState === 'loaded' && metrics !== null">
          {{ formatCount(metrics.throttles) }}
        </ng-container>
        <ng-container *ngIf="metricsState === 'unavailable'">
          <span class="text-slate-300">—</span>
        </ng-container>
      </p>
      <p class="mt-1 text-[11px]"
         [class.text-amber-400]="metricsState === 'loaded' && metrics !== null && metrics.throttles > 0"
         [class.text-slate-400]="!(metricsState === 'loaded' && metrics !== null && metrics.throttles > 0)">
        <ng-container *ngIf="metricsState === 'unavailable'">No metric data</ng-container>
        <ng-container *ngIf="metricsState !== 'unavailable'">Last 24 hours</ng-container>
      </p>
    </div>

  </div>

</div>

<!-- ── Functions ─────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'functions'" role="tabpanel">

  <!-- Filter bar spans full width -->
  <app-cloud-filter-bar
    entityName="functions"
    placeholder="Search function name…"
    [facets]="facets"
    [sortOptions]="sortOptions"
    [filters]="filters"
    [resultCount]="filteredFunctions.length"
    [totalCount]="functions.length"
    [quotaUsage]="quotaUsage"
    (filtersChange)="onFiltersChange($event)"
  ></app-cloud-filter-bar>

  <!-- Content row: table + optional function detail panel -->
  <div class="flex min-h-0" [class.divide-x]="selectedFunction" [class.divide-slate-200]="selectedFunction">

    <!-- Table area -->
    <div class="min-w-0 flex-1 p-6">

      <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading functions…
      </div>

      <div *ngIf="!loading" class="overflow-x-auto">
        <table class="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr class="border-b border-slate-200">
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Function</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Runtime</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Memory</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Timeout</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Issues (24h)</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Modified</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Routing</th>
              <th class="pb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let fn of pagedFunctions"
                class="border-b border-slate-100 last:border-0"
                [class.bg-violet-50]="fn.name === selectedFunction">
              <td class="py-3 pr-4 font-mono text-xs font-medium text-slate-900">{{ fn.name }}</td>
              <td class="py-3 pr-4 text-xs text-slate-600">{{ fn.runtime }}</td>
              <td class="py-3 pr-4 text-xs tabular-nums text-slate-700">{{ fn.memoryMB }} MB</td>
              <td class="py-3 pr-4 text-xs tabular-nums text-slate-600">{{ fn.timeoutSec }}s</td>
              <td class="py-3 pr-4"><app-status-badge [status]="fn.status"></app-status-badge></td>
              <td class="py-3 pr-4 text-xs tabular-nums whitespace-nowrap"
                  [title]="fn.invocations24h + ' invocations (24h)'">
                <ng-container *ngIf="fn.errors24h > 0 || fn.throttles24h > 0; else noIssues">
                  <span *ngIf="fn.errors24h > 0"
                        class="mr-2 font-semibold text-red-600">E {{ formatCount(fn.errors24h) }}</span>
                  <span *ngIf="fn.throttles24h > 0"
                        class="font-semibold text-amber-600">T {{ formatCount(fn.throttles24h) }}</span>
                </ng-container>
                <ng-template #noIssues>
                  <span class="text-slate-300">—</span>
                </ng-template>
              </td>
              <td class="py-3 pr-4 text-xs text-slate-500">{{ fn.lastModified | cloudDate }}</td>
              <!-- Monitoring -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <ng-container *ngIf="isMonitored(fn.name); else fnNotMonitored">
                  <button
                    type="button"
                    (click)="toggleMonitoring(fn.name)"
                    class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    aria-label="Disable monitoring"
                  >
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                    Monitored
                  </button>
                </ng-container>
                <ng-template #fnNotMonitored>
                  <ng-container *ngIf="canMonitorMore; else fnQuotaFull">
                    <button
                      type="button"
                      (click)="toggleMonitoring(fn.name)"
                      class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    >
                      Monitor
                    </button>
                  </ng-container>
                  <ng-template #fnQuotaFull>
                    <span class="group relative inline-block">
                      <button
                        type="button"
                        (click)="toggleMonitoring(fn.name)"
                        aria-disabled="true"
                        class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none"
                      >
                        Monitor
                      </button>
                      <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        Quota reached
                      </span>
                    </span>
                  </ng-template>
                </ng-template>
              </td>
              <!-- Routing: override indicator + gear -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <div class="flex items-center gap-1.5">
                  <span *ngIf="itemOverrideSummary(fn.name)"
                        class="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                    {{ itemOverrideSummary(fn.name) }}
                  </span>
                  <button
                    type="button"
                    (click)="openItemSettings(fn.name)"
                    class="flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    [class.text-violet-500]="!!itemOverrideSummary(fn.name)"
                    [attr.aria-label]="'Configure routing for ' + fn.name"
                  >
                    <lucide-icon name="settings" [size]="12" [strokeWidth]="2"></lucide-icon>
                  </button>
                </div>
              </td>
              <td class="py-3 text-right">
                <button
                  (click)="fn.name === selectedFunction ? closeFunction() : selectFunction(fn.name)"
                  class="rounded px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.text-violet-600]="fn.name === selectedFunction"
                  [class.hover:text-violet-700]="fn.name === selectedFunction"
                >
                  {{ fn.name === selectedFunction ? 'Close' : 'View details' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty state: no matches -->
        <div *ngIf="filteredFunctions.length === 0 && functions.length > 0" class="py-10 text-center">
          <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
          <p class="text-sm font-medium text-slate-500">No functions match your filters</p>
          <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
        </div>

        <!-- Empty state: no data -->
        <p *ngIf="functions.length === 0" class="py-6 text-sm text-slate-400">
          No Lambda functions found in this account and region.
        </p>

        <!-- Pagination (unchanged) -->
        <div *ngIf="totalPages > 1" class="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
          <p class="text-xs text-slate-400">{{ pageStart }}–{{ pageEnd }} of {{ filteredFunctions.length }}</p>
          <div class="flex items-center gap-1">

            <button
              (click)="prevPage()"
              [disabled]="currentPage === 1"
              class="rounded px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
              [class.cursor-not-allowed]="currentPage === 1"
              [class.text-slate-300]="currentPage === 1"
              [class.text-slate-600]="currentPage !== 1"
              [class.hover:bg-slate-100]="currentPage !== 1"
            >
              Previous
            </button>

            <button
              *ngFor="let p of pageNumbers"
              (click)="goToPage(p)"
              class="min-w-[28px] rounded px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
              [class.bg-violet-600]="p === currentPage"
              [class.text-white]="p === currentPage"
              [class.text-slate-600]="p !== currentPage"
              [class.hover:bg-slate-100]="p !== currentPage"
            >
              {{ p }}
            </button>

            <button
              (click)="nextPage()"
              [disabled]="currentPage === totalPages"
              class="rounded px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
              [class.cursor-not-allowed]="currentPage === totalPages"
              [class.text-slate-300]="currentPage === totalPages"
              [class.text-slate-600]="currentPage !== totalPages"
              [class.hover:bg-slate-100]="currentPage !== totalPages"
            >
              Next
            </button>

          </div>
        </div>
      </div>

    </div>

    <!-- Function detail panel -->
    <div *ngIf="selectedFunction" class="flex w-96 shrink-0 flex-col">

      <!-- Panel header -->
      <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div class="flex min-w-0 items-center gap-2">
          <lucide-icon name="zap" [size]="14" class="shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
          <span class="truncate font-mono text-xs font-semibold text-slate-800">{{ selectedFunction }}</span>
        </div>
        <button
          (click)="closeFunction()"
          class="ml-2 shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
          aria-label="Close function details"
        >
          <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
        </button>
      </div>

      <!-- Function config summary -->
      <div *ngIf="selectedFunctionData" class="border-b border-slate-100 px-4 py-3">
        <div class="grid grid-cols-2 gap-x-4 gap-y-2">
          <div>
            <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Runtime</p>
            <p class="mt-0.5 text-xs text-slate-700">{{ selectedFunctionData.runtime }}</p>
          </div>
          <div>
            <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Status</p>
            <div class="mt-0.5"><app-status-badge [status]="selectedFunctionData.status"></app-status-badge></div>
          </div>
          <div>
            <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Memory</p>
            <p class="mt-0.5 text-xs tabular-nums text-slate-700">{{ selectedFunctionData.memoryMB }} MB</p>
          </div>
          <div>
            <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Timeout</p>
            <p class="mt-0.5 text-xs tabular-nums text-slate-700">{{ selectedFunctionData.timeoutSec }}s</p>
          </div>
          <div class="col-span-2">
            <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Last Modified</p>
            <p class="mt-0.5 text-xs text-slate-700">{{ selectedFunctionData.lastModified | cloudDate }}</p>
          </div>
        </div>
      </div>

      <!-- 24h metrics summary -->
      <div class="border-b border-slate-100 px-4 py-3">
        <p class="mb-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">Last 24 Hours</p>
        <div class="grid grid-cols-3 gap-2">

          <div class="rounded-md border border-slate-200 bg-slate-50 px-2 py-2 text-center">
            <p class="text-[10px] text-slate-400">Invocations</p>
            <p class="mt-0.5 text-sm font-bold text-slate-900">
              <span *ngIf="fnMetricsState === 'loading'" class="animate-pulse text-slate-300">—</span>
              <ng-container *ngIf="fnMetricsState === 'loaded' && fnMetrics">{{ formatCount(fnMetrics.totalInvocations) }}</ng-container>
              <span *ngIf="fnMetricsState === 'unavailable'" class="text-slate-300">—</span>
            </p>
          </div>

          <div class="rounded-md border px-2 py-2 text-center"
               [class.border-red-200]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0"
               [class.bg-red-50]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0"
               [class.border-slate-200]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0)"
               [class.bg-slate-50]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0)">
            <p class="text-[10px]"
               [class.text-red-500]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0"
               [class.text-slate-400]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0)">Errors</p>
            <p class="mt-0.5 text-sm font-bold"
               [class.text-red-700]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0"
               [class.text-slate-900]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalErrors > 0)">
              <span *ngIf="fnMetricsState === 'loading'" class="animate-pulse text-slate-300">—</span>
              <ng-container *ngIf="fnMetricsState === 'loaded' && fnMetrics">{{ formatCount(fnMetrics.totalErrors) }}</ng-container>
              <span *ngIf="fnMetricsState === 'unavailable'" class="text-slate-300">—</span>
            </p>
          </div>

          <div class="rounded-md border px-2 py-2 text-center"
               [class.border-amber-200]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0"
               [class.bg-amber-50]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0"
               [class.border-slate-200]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0)"
               [class.bg-slate-50]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0)">
            <p class="text-[10px]"
               [class.text-amber-500]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0"
               [class.text-slate-400]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0)">Throttles</p>
            <p class="mt-0.5 text-sm font-bold"
               [class.text-amber-700]="fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0"
               [class.text-slate-900]="!(fnMetricsState === 'loaded' && fnMetrics && fnMetrics.totalThrottles > 0)">
              <span *ngIf="fnMetricsState === 'loading'" class="animate-pulse text-slate-300">—</span>
              <ng-container *ngIf="fnMetricsState === 'loaded' && fnMetrics">{{ formatCount(fnMetrics.totalThrottles) }}</ng-container>
              <span *ngIf="fnMetricsState === 'unavailable'" class="text-slate-300">—</span>
            </p>
          </div>

        </div>
      </div>

      <!-- Hourly activity section label -->
      <p class="border-b border-slate-100 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        Hourly Activity (Last 24h)
      </p>

      <!-- Panel body: activity table -->
      <div class="flex-1 overflow-y-auto">

        <!-- Loading -->
        <div *ngIf="fnMetricsState === 'loading'" class="flex items-center gap-2 px-4 py-6 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading…
        </div>

        <!-- Unavailable -->
        <p *ngIf="fnMetricsState === 'unavailable'" class="px-4 py-4 text-xs text-slate-400">
          CloudWatch metric data is unavailable for this function.
        </p>

        <!-- Activity list -->
        <div *ngIf="fnMetricsState === 'loaded' && fnMetrics">

          <!-- Empty: no activity in last 24h -->
          <div *ngIf="fnMetrics.slots.length === 0" class="px-4 py-6 text-sm text-slate-400">
            No activity in the last 24 hours.
          </div>

          <!-- Table of hourly slots -->
          <div *ngIf="fnMetrics.slots.length > 0" class="overflow-x-auto">
            <table class="w-full text-left text-[11px]">
              <thead>
                <tr class="border-b border-slate-100">
                  <th class="px-4 pb-2 pt-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Time</th>
                  <th class="px-2 pb-2 pt-3 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">Inv</th>
                  <th class="px-2 pb-2 pt-3 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">Err</th>
                  <th class="px-2 pb-2 pt-3 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">Thr</th>
                  <th class="px-4 pb-2 pt-3 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">Avg</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let slot of pagedFnSlots"
                    class="border-b border-slate-100 last:border-0"
                    [class.bg-red-50]="slot.errors > 0">
                  <td class="py-2 pl-4 pr-2 text-slate-600">{{ slot.timestamp | cloudDate }}</td>
                  <td class="px-2 py-2 text-right tabular-nums text-slate-700">{{ slot.invocations }}</td>
                  <td class="px-2 py-2 text-right tabular-nums"
                      [class.font-semibold]="slot.errors > 0"
                      [class.text-red-600]="slot.errors > 0"
                      [class.text-slate-500]="slot.errors === 0">{{ slot.errors }}</td>
                  <td class="px-2 py-2 text-right tabular-nums"
                      [class.text-amber-600]="slot.throttles > 0"
                      [class.text-slate-500]="slot.throttles === 0">{{ slot.throttles }}</td>
                  <td class="py-2 pl-2 pr-4 text-right tabular-nums text-slate-500">{{ formatMs(slot.avgDurationMs) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Panel pagination -->
          <div *ngIf="fnSlotsTotalPages > 1"
               class="flex items-center justify-between border-t border-slate-100 px-4 py-2">
            <p class="text-[11px] text-slate-400">
              Page {{ fnSlotsPage }} of {{ fnSlotsTotalPages }}
            </p>
            <div class="flex items-center gap-1">
              <button
                (click)="fnSlotsPage = fnSlotsPage - 1"
                [disabled]="fnSlotsPage === 1"
                class="rounded px-2 py-1 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.text-slate-300]="fnSlotsPage === 1"
                [class.cursor-not-allowed]="fnSlotsPage === 1"
                [class.text-slate-600]="fnSlotsPage !== 1"
                [class.hover:bg-slate-100]="fnSlotsPage !== 1"
              >Prev</button>
              <button
                (click)="fnSlotsPage = fnSlotsPage + 1"
                [disabled]="fnSlotsPage === fnSlotsTotalPages"
                class="rounded px-2 py-1 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.text-slate-300]="fnSlotsPage === fnSlotsTotalPages"
                [class.cursor-not-allowed]="fnSlotsPage === fnSlotsTotalPages"
                [class.text-slate-600]="fnSlotsPage !== fnSlotsTotalPages"
                [class.hover:bg-slate-100]="fnSlotsPage !== fnSlotsTotalPages"
              >Next</button>
            </div>
          </div>

        </div>

      </div>
    </div>

  </div>

</div>

<!-- ── Activity ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'activity'" role="tabpanel">
  <app-cloud-activity-feed
    [items]="activity"
    [loading]="activityLoading"
    serviceLabel="Lambda"
    emptyHint="Invocation-failure, throttle, and unhealthy-function findings appear here."
    [note]="'Activity is derived from monitoring findings — per-invocation traces aren\'t collected.'"
    (itemSelect)="onActivitySelect($event)"
  ></app-cloud-activity-feed>
</div>

<!-- ── Item settings drawer ──────────────────────────────────────── -->
<app-item-settings-drawer
  [service]="serviceCard"
  [itemId]="drawerItemId"
  [open]="drawerItemId !== null"
  (closed)="closeItemSettings()"
></app-item-settings-drawer>
